import { useCallback, useState } from "react";
import {
  DEFAULT_PRINT_SETTINGS,
  type ReaderSettings,
  normalizeNevisignApiBase,
} from "@nevisign/reader-core";
import {
  DEFAULT_NEVISIGN_API_BASE,
  LEGACY_AIHUB_API_BASE,
  LEGACY_IP_API_BASE,
} from "../lib/constants";

const STORAGE_KEY = "nevisign-reader-settings";

const defaults: ReaderSettings = {
  nevisignApiBase:
    (import.meta.env.VITE_NEVISIGN_API_BASE as string | undefined)?.trim() ||
    DEFAULT_NEVISIGN_API_BASE,
  showTrackChanges: true,
  showComments: true,
  docxViewMode: "continuous",
  print: { ...DEFAULT_PRINT_SETTINGS },
};

function normalizeApiBase(base: string): string {
  const trimmed = base.trim().replace(/\/$/, "");
  if (
    !trimmed ||
    trimmed === LEGACY_IP_API_BASE ||
    trimmed === LEGACY_AIHUB_API_BASE ||
    trimmed === "https://37.32.27.208" ||
    trimmed === "http://37.32.27.208/nevisign" ||
    trimmed === "https://aihub.mohaami.ir" ||
    trimmed.startsWith("https://aihub.mohaami.ir/") ||
    trimmed === "https://mohaami.ir/nevisign"
  ) {
    return DEFAULT_NEVISIGN_API_BASE;
  }
  return normalizeNevisignApiBase(trimmed);
}

function load(): ReaderSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaults, print: { ...DEFAULT_PRINT_SETTINGS } };
    const parsed = JSON.parse(raw) as Partial<ReaderSettings>;
    const next: ReaderSettings = {
      ...defaults,
      ...parsed,
      nevisignApiBase: normalizeApiBase(
        parsed.nevisignApiBase ?? defaults.nevisignApiBase,
      ),
      print: {
        ...DEFAULT_PRINT_SETTINGS,
        ...(parsed.print ?? {}),
      },
    };
    if (parsed.nevisignApiBase !== next.nevisignApiBase) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    }
    return next;
  } catch {
    return { ...defaults, print: { ...DEFAULT_PRINT_SETTINGS } };
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<ReaderSettings>(() => load());

  const updateSettings = useCallback((partial: Partial<ReaderSettings>) => {
    setSettings((prev) => {
      const next: ReaderSettings = {
        ...prev,
        ...partial,
        print: partial.print
          ? { ...prev.print, ...partial.print }
          : prev.print,
      };
      if (partial.nevisignApiBase != null) {
        next.nevisignApiBase = normalizeApiBase(partial.nevisignApiBase);
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return { settings, updateSettings };
}
